import {
  EditOutlined,
  InfoCircleFilled,
  QuestionCircleOutlined,
  WarningFilled,
} from '@ant-design/icons';
import { Button } from '@mm/components';
import { useCanResolveDomain, useResolutionInfo } from '@mm/hooks';
import { trafficTotalSelector } from '@mm/state/selectors/websiteReportSummarySelector';
import classNames from 'classnames';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { TABLET_SMALL } from '../../../../editor/constants/screenSize';
import { CaseStudies } from '../CaseStudies/CaseStudies';
import { HowXRayWorksVersionB } from '../HowXRayWorksVersionB/HowXRayWorksVersionB';
import {
  LLRActionButtons,
  LLRActionButtonsProps,
} from '../LLRActionButtons/LLRActionButtons';
import { MetaEventsTableMock } from '../MetaEventsTableMock/MetaEventsTableMock';
import { Brands } from '../Partners/Brands';
import { QualityScore } from '../QualityScore/QualityScore';
import { ReportFooter } from '../ReportFooter/ReportFooter';
import { SignalLossDiagram } from '../SignalLossDiagram/SignalLossDiagram';
import { WebsiteScreenshot } from '../WebsiteScreenshot/WebsiteScreenshot';
import formTwoSrc from './Icons/blurred-form.png';
import formSrc from './Icons/form.png';
import { PdfReportButton } from './PdfReportButton/PdfReportButton';
import { ReportHeader } from './ReportHeader/ReportHeader';
import { LLRReportVersion } from './WebsiteReport';
import styles from './WebsiteReportSummaryVersionB.module.css';

// tslint:disable-next-line: cyclomatic-complexity
export const WebsiteReportSummaryVersionB = ({
  version,
  setVersion,
  websiteURL,
  openSignupModal,
  openEditUrlModal,
  isWhiteLabel,
}: {
  version: LLRReportVersion;
  setVersion: (version: LLRReportVersion) => void;
  websiteURL: string;
  openSignupModal: () => void;
  openEditUrlModal: () => void;
  isWhiteLabel?: boolean;
}) => {
  const { screenWidth } = useResolutionInfo();
  const isMobile = screenWidth <= TABLET_SMALL;
  const { canResolveDomain } = useCanResolveDomain({
    websiteURL,
    initialValue: true,
  });

  const totalTraffic = useSelector(trafficTotalSelector);
  const organicTrafficTotal = totalTraffic.trafficType?.organic || 0;
  const paidTrafficTotal = totalTraffic.trafficType?.paid || 0;
  const auth = THUM_IO_AUTH_KEY ? `/auth/${THUM_IO_AUTH_KEY}` : '';
  const screenshotSrc = `https://image.thum.io/get${auth}/width/384/https://${websiteURL}`;
  const trafficSum = organicTrafficTotal + paidTrafficTotal;

  const [pageView, setPageView] = useState<number>(4.5);

  const history = useHistory();
  const updateVersion = () => {
    setVersion('A');
    history.replace(history.location.pathname.replace(version, 'A'));
  };

  const renderActionButtons = (
    restProps?: Pick<
      LLRActionButtonsProps,
      'buttonName' | 'withFBBadges' | 'css' | 'iconProps'
    >,
  ) =>
    isWhiteLabel ? null : (
      <LLRActionButtons
        traffic={trafficSum}
        className={styles.actionsButtons}
        websiteURL={websiteURL}
        buttonName={
          restProps?.buttonName || 'Restore My Lost Meta Pixel Signal'
        }
        onClick={openSignupModal}
        iconProps={restProps?.iconProps}
        withNote={false}
        withFBBadges
        {...restProps}
      />
    );

  return (
    <div className={classNames(styles.wrapper)}>
      {!isMobile && !isWhiteLabel && (
        <div className={styles.widget}>
          <img src={formSrc} className={styles.widgetImageOne} />
          <img src={formTwoSrc} className={styles.widgetImageTwo} />
          <img src={formTwoSrc} className={styles.widgetImageThree} />
          <Button
            onClick={updateVersion}
            color="secondary-blue"
            css={{ container: styles.widgetButton }}
          >
            View Email Report
          </Button>
        </div>
      )}

      {!isWhiteLabel && (
        <ReportHeader
          activeVersion={version}
          setActiveVersion={setVersion}
          isMobile={isMobile}
        />
      )}

      <div className={styles.container}>
        <section className={classNames(styles.mainPanel, styles.section)}>
          <div className={classNames(styles.column, styles.title)}>
            <div className={styles.heading}>
              <span className={styles.websiteUrl}>{websiteURL}</span> 's Meta
              Ads event match quality score
            </div>
            <div className={styles.subHeading}>
              <span
                className={classNames(styles.websiteUrl, styles.slimDecoration)}
              >
                {websiteURL}
              </span>
              's event match quality is{' '}
              <span className={styles.importantHighligted}>20% lower</span> than
              industry average and the Meta benchmark. This is affecting your
              attribution, retargeting, and overall performance.
            </div>
            <div className={styles.subHeading}>
              <span
                className={classNames(styles.websiteUrl, styles.slimDecoration)}
              >
                {websiteURL}
              </span>{' '}
              is overpaying for impressions and conversions is{' '}
              <span className={styles.importantHighligted}>
                Costing you money.
              </span>
            </div>
          </div>
          <div className={classNames(styles.row, styles.websiteOverview)}>
            <div className={styles.left}>
              <div className={styles.websiteUrl}>
                {websiteURL}{' '}
                <EditOutlined
                  className={styles.editIcon}
                  onClick={openEditUrlModal}
                />
              </div>
              <div className={styles.screenshotBlock}>
                <WebsiteScreenshot
                  canResolve={canResolveDomain}
                  websiteURL={websiteURL}
                  src={screenshotSrc}
                  css={{ container: styles.screenshot }}
                />
              </div>
            </div>
            <div className={styles.right}>
              <div className={classNames(styles.alert, styles.alertOne)}>
                {' '}
                <WarningFilled className={styles.warningIcon} />
                Your Facebook Match score needs improvement
              </div>
              <MetaEventsTableMock
                traffic={trafficSum}
                pageView={pageView}
                setPageView={setPageView}
              />
              <div
                className={classNames(
                  styles.row,
                  styles.alignCenter,
                  styles.help,
                )}
              >
                <div className={classNames(styles.alert, styles.warning)}>
                  <InfoCircleFilled className={styles.infoCircule} />
                  These are estimated numbers, you can update them manually
                </div>
                <div
                  className={classNames(
                    styles.row,
                    styles.alignCenter,
                    styles.infoLink,
                  )}
                >
                  <QuestionCircleOutlined style={{ fontSize: 14 }} /> Where do I
                  find these events?
                </div>
              </div>
              <div className={styles.alerts}>
                <div className={classNames(styles.alert, styles.alertTwo)}>
                  Ad performance is suffering
                </div>
                <div
                  className={classNames(
                    styles.column,
                    styles.alert,
                    styles.alertThree,
                    styles.alignStart,
                    styles.gap8,
                  )}
                >
                  <span className={styles.importantHighligted}>+33.4%</span>

                  <span className={styles.column}>
                    <span style={{ fontWeight: 400 }}>New Sales Ad Set</span>
                    Cost per result skyrocketing
                  </span>
                </div>
              </div>
            </div>
          </div>
          {renderActionButtons()}
        </section>

        <section
          className={classNames(
            styles.section,
            styles.column,
            styles.alignCenter,
            styles.qualityScorePanel,
          )}
        >
          <div className={styles.heading}>
            How does the Facebook Event Match Quality Score Affect{' '}
            <span className={styles.websiteUrl}>{websiteURL}</span> Ads?
          </div>
          <div className={styles.subHeading}>
            Facebook uses event match quality scores, derived from pixel and
            server-side data, to optimize Advantage + campaigns, attribute
            sales, and identify profitable audiences for advertising.
            <br />
            <br />
            The more information FB gets the better your ads run!
          </div>
          <QualityScore
            website={websiteURL}
            pageView={pageView}
            isMobile={isMobile}
            isWhiteLabel={isWhiteLabel}
          />

          <div className={styles.ctaBlock}>
            Connect your ad account to evaluate the $ impact on your CPC based
            on your event match quality score
            {renderActionButtons()}
          </div>
        </section>
        <Brands />

        <section
          className={classNames(
            styles.section,
            styles.column,
            styles.alignCenter,
            styles.signalLossPanel,
          )}
        >
          <div className={styles.heading}>
            Visualizing your signal loss due to changing browser and device
            environments
          </div>
          <SignalLossDiagram
            website={websiteURL}
            traffic={trafficSum}
            isMobile={isMobile}
          />
          {renderActionButtons({ buttonName: 'Fight Signal Loss Now' })}
        </section>

        <section
          className={classNames(styles.column, styles.section, styles.adsPanel)}
        >
          <div className={styles.heading}>
            How does the event match quality score affect the cost of my ads?
          </div>
          <div className={styles.subHeading}>
            You are getting charged more — you are showing thee ads to people
            who are not the best
          </div>
          <HowXRayWorksVersionB isWhiteLabel={isWhiteLabel} />
          <div className={styles.ctaBlock}>
            Connect your ad account to evaluate the $ impact on your CPC based
            on your Quality Score
            {renderActionButtons()}
          </div>
        </section>

        <section
          className={classNames(
            styles.column,
            styles.section,
            styles.caseStudiesPanel,
          )}
        >
          <div className={styles.heading}>Case Studies</div>
          <CaseStudies isWhiteLabel={isWhiteLabel} />
        </section>
        <section
          className={classNames(styles.column, styles.section, styles.footer)}
        >
          <ReportFooter
            renderActionButtons={renderActionButtons}
            isWhiteLabel={isWhiteLabel}
          />
        </section>

        <PdfReportButton isWhiteLabel={isWhiteLabel} version={version} />
      </div>
    </div>
  );
};
